<!--
Provides a base text input that can be reused to create more complex components.
 -->
<script setup lang="ts">import { ref as _ref, computed as _computed, toRef as _toRef } from 'vue';
const overrides = __MACROS_toRef(__props, "class");
const originalType = __MACROS_toRef(__props, "type");

import { toRef as __MACROS_toRef } from "vue";const __props_modelValue = _toRef(__props, 'modelValue');

import { removeDecimals } from '@corp/helpers/number'
import { isEmpty } from '@corp/helpers/object'
import { useEventListener } from '@vueuse/core'
import { keyFor } from '@corp/helpers/events'

export interface XTextInputProps {
  // The input type of the field.
  type?: string

  // Whether to use the native number type, which allows decimal.
  useNativeNumber?: boolean

  // The value of the input.
  modelValue?: any

  // The size of the input.
  size?: 'sm' | 'lg'

  // Whether the field has errors.
  hasErrors?: boolean

  // Whether the field is disabled.
  disabled?: boolean

  // Whether the field is required.
  required?: boolean | string

  // Whether the input should be a textarea.
  multiline?: boolean

  // Prefix to add to the field.
  prefix?: string

  // Styles for the wrapper and the input.
  class?: any

  // Provide a custom way to handle a click in the wrapper.
  focusInput?: ((inputEl: HTMLInputElement | undefined, event: Event) => void)

  // Maximum height of the input.
  maxHeight?: string
}

withDefaults(defineProps<XTextInputProps>(), { type: 'text',size: 'lg', })

const attrs = useAttrs()

const emit = defineEmits<{
  file: [File | undefined]
  'update:modelValue': [value: any, errorMessage: undefined, validity: ValidityState]
}>()

const inputEl = _ref<HTMLInputElement>(undefined as any)

const focused = _ref(false)

const isNumeric = __props.type === 'number' && !__props.useNativeNumber

// Let the browser enforce max length.
const type = isNumeric ? 'text' : attrs.domType || __props.type

// Remove non-significant decimals added by the backend.
const value = isNumeric
  ? computed(() => removeDecimals(__props.modelValue))
  : (__props_modelValue)

// Prevent entering anything except numbers (like letters and decimal separators).
//
// NOTE: Currently we don't allow fractional values for any amounts.
if (isNumeric) {
  useEventListener((inputEl), 'keypress', (event: KeyboardEvent) => {
    const key = keyFor(event)
    if (key !== String(Number(key)) && key !== 'Enter')
      event.preventDefault()
  })

  useEventListener((inputEl), 'paste', (event: ClipboardEvent) => {
    event.preventDefault()
    const pasteValue = event.clipboardData?.getData('text') || ''
    emit('update:modelValue', pasteValue.replace(/[^0-9]/g, ''), undefined, inputEl.value?.validity)
  })
}

defineExpose({
  get inputEl () {
    return inputEl.value
  },
  focus () {
    inputEl.value?.focus()
  },
  select () {
    inputEl.value?.select()
  },
})

function autoExpandTextarea () {
  if (__props.multiline) {
    inputEl.value.style.height = 'auto'
    inputEl.value.style.height = `${inputEl.value.scrollHeight}px`
  }
}

function onInput (event: InputEvent) {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target?.value, undefined, inputEl.value?.validity)
  autoExpandTextarea()
}

function onChange (event: Event) {
  if (type === 'file')
    emit('file', inputEl.value?.files?.[0])
}

function onMousedown (event: Event) {
  if (event.defaultPrevented)
    return

  event.preventDefault()

  if (__props.focusInput)
    __props.focusInput(inputEl.value, event)
  else
    inputEl.value?.focus()
}

const sizeStyle = _computed(() => __props.size === 'lg' ? 'text-md rounded-lg' : 'text-xs rounded-md')
const style = _computed(() => __props.maxHeight ? { maxHeight: __props.maxHeight, overflow: 'auto' } : {})
const background = _computed(() => __props.disabled ? 'bg-warm-200 text-warm-500' : 'bg-white')

const $slots = useSlots()
const hasSlots = _computed(() => Boolean(__props.prefix) || !isEmpty($slots))
const displayPrefix = _computed(() => Boolean(__props.prefix && (__props.modelValue || inputEl.value?.validity?.badInput)))

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <div
    class="text-input relative flex items-center"
    :class="[
      {
        focused,
        disabled,
        invalid: hasErrors,
        padded: hasSlots,
      },
      background,
      size,
      sizeStyle,
      overrides,
    ]"
    :style="style"
    @pointerdown.self="onMousedown"
  >
    <slot name="prefix">
      <span v-if="displayPrefix" class="pointer-events-none">{{ prefix }}</span>
    </slot>
    <slot name="inner"/>
    <slot name="input">
      <component
        :is="multiline ? 'textarea' : 'input'"
        ref="inputEl"
        class="inner-input outline-none border-none flex-grow w-full"
        :class="[sizeStyle, overrides, background, { padded: !hasSlots }]"
        :type="type"
        :disabled="disabled"
        :rows="multiline ? ($attrs.rows || 3) : undefined"
        :value="value"
        :autocomplete="$attrs.autocomplete || 'off'"
        v-bind="$attrs"
        @focus="focused = true"
        @blur="focused = false"
        @input="onInput"
        @change="onChange"
      />
    </slot>
    <slot name="suffix"/>
  </div>
</template>

<style scoped lang="scss">
.sm {
  --py: 9px;
  --input-height: 2.5rem;
}

.lg {
  --py: 14px;
  --input-height: 3.5rem;
}

.text-input {
  --px: calc(theme("spacing.DEFAULT") / 1.6);
  --pl: var(--px);
  --pr: var(--suffix-width, var(--px));

  border: 2px solid var(--input-br, theme("colors.warm.300"));
  cursor: var(--cursor, text);
  min-height: var(--input-height);

  &.focused,
  &:hover {
    --input-br: theme("colors.dark.500");
  }

  &.invalid {
    --input-br: theme("colors.red.300");
  }

  &.disabled {
    --cursor: "pointer";
    --input-br: theme("colors.warm.300");
  }
}

.focused {
  box-shadow: 0 0 0 0.5px var(--input-br);
}

.inner-input {
  cursor: inherit;
  padding: var(--py) 0;

  &::placeholder {
    opacity: 0.6;
  }
}

.padded {
  padding-left: var(--pl);
  padding-right: var(--pr);
}

/* Hide the native arrows in number inputs */
input[type="number"] {
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
}
</style>
